import React, { createContext, useContext, useEffect, useState, useMemo } from "react";
import { api, formatApiError } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = not auth, object = user
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        if (!cancelled) setUser(data);
      } catch {
        if (!cancelled) setUser(false);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const value = useMemo(() => {
    const refresh = async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch (err) {
        console.error("Auth refresh failed:", err);
        setUser(false);
      }
    };

    const login = async (email, password) => {
      try {
        const { data } = await api.post("/auth/login", { email, password });
        setUser(data);
        return { ok: true, user: data };
      } catch (e) {
        return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
      }
    };

    const register = async (name, email, password) => {
      try {
        const { data } = await api.post("/auth/register", { name, email, password, role: "customer" });
        setUser(data);
        return { ok: true, user: data };
      } catch (e) {
        return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
      }
    };

    const logout = async () => {
      try {
        await api.post("/auth/logout");
      } catch (err) {
        console.error("Logout request failed:", err);
      }
      setUser(false);
    };

    return { user, loading, login, register, logout, refresh };
  }, [user, loading]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
